import { v4 as uuidv4 } from "uuid";

import type { MoreLikeThisSearchData } from "@/components/ui/MoreLikeThisModal";
import { clientGeneratedFacet } from "@/lib/facet-provenance";

type MltPayload = Omit<MoreLikeThisSearchData, "freeformInput">;

function mapUserGenderToFacetValue(storedGender: string): "men" | "women" | "all" {
  if (storedGender === "mens") return "men";
  if (storedGender === "womens") return "women";
  return "all";
}

/**
 * Builds an MLT payload from search data, injecting the user's gender facet
 * from localStorage when applicable.
 */
function buildMltPayload(searchData: MoreLikeThisSearchData): MltPayload {
  let userGender = "unisex";
  try {
    userGender = localStorage.getItem("userGender") || "unisex";
  } catch {
    // localStorage unavailable (e.g., incognito mode, sandboxed iframe)
  }
  const gender = mapUserGenderToFacetValue(userGender);
  const facets = [...(searchData.facets || [])];
  if (gender !== "all") {
    // A preference persisted in localStorage, applied to every later search:
    // an inference about the shopper, not a filter they set on this one.
    facets.push(clientGeneratedFacet("gender", [gender]));
  }

  return {
    sourceUuid: searchData.sourceUuid,
    styles: searchData.styles || [],
    tags: searchData.tags || [],
    facets,
    exclusionFacets: searchData.exclusionFacets || [],
    pricePreference: searchData.pricePreference,
    productPrice: searchData.productPrice,
  };
}

/**
 * Dispatches an MLT search. If an existing thread is active (results page),
 * fires a custom DOM event for in-place update. Otherwise stores the payload
 * in sessionStorage and returns the path to navigate to.
 */
export function dispatchMltSearch(
  searchData: MoreLikeThisSearchData,
  existingThreadId?: string
): { navigateTo?: string } {
  const payload = buildMltPayload(searchData);

  if (existingThreadId) {
    window.dispatchEvent(
      new CustomEvent("mlt-search", {
        detail: { threadId: existingThreadId, ...payload },
      })
    );
    return {};
  }

  const threadId = uuidv4();
  try {
    sessionStorage.setItem(`mlt-${threadId}`, JSON.stringify(payload));
  } catch {
    // sessionStorage unavailable — navigation still occurs, but results page
    // may not find pre-populated MLT params for this thread.
  }
  return { navigateTo: `/results/${threadId}` };
}
